"use client";
import PlayBar from "./PlayBar";
import CorePlayer from "./CorePlayer";
import ReactPlayer from "react-player";
import Logo from "../assets/images/logo-white.png";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { usePodcasts } from "../features/podcast/hooks/usePodcasts";

type MediaPlayerProps = {
  onToggleCollapsed?: () => void;
  isCollapsed?: boolean;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  onStopPlay?: () => void;
  externalTrackIndex?: number;
  onExternalTrackIndexChange?: (index: number) => void;
  selectedTrackId?: number;
  setSelectedTrackId: (id: number | undefined) => void;
  isMobileOpen: boolean;
  setIsMobileOpen: (v: boolean) => void;
  setIsPlayerVisible?: (v: boolean) => void;
};

export default function MediaPlayer({
  onToggleCollapsed,
  isPlaying = false,
  onTogglePlay,
  onStopPlay,
  externalTrackIndex,
  onExternalTrackIndexChange,
  selectedTrackId,
  setSelectedTrackId,
  isMobileOpen,
  setIsMobileOpen,
  setIsPlayerVisible,
}: MediaPlayerProps) {
  const playerRef = useRef<any>(null);
  const isControlled = typeof onTogglePlay === "function";
  const [internalPlaying, setInternalPlaying] = useState(false);
  const { data: podcastsRes } = usePodcasts();
  const podcasts = useMemo(() => podcastsRes?.data ?? [], [podcastsRes?.data]);
  const handleClosePlayer = () => {
    onStopPlay?.();
    setSelectedTrackId?.(undefined);
    setProgress(0);
    setDuration(0);
    setIsMobileOpen(false);
    setIsPlayerVisible?.(false);
  };
  const tracks = useMemo(
    () =>
      podcasts
        ?.map((item) => {
          const src = item.files?.[0]?.url;
          if (!src || typeof src !== "string" || !src.trim()) return null;

          return {
            id: item.id,
            title: item.name,
            subtitle: item.meta_text ?? "",
            src: src.trim(),
            cover: item.cover ?? Logo.src,
            durationLabel: item.duration ?? "",
            date: item.date,
          };
        })
        .filter((t): t is NonNullable<typeof t> => t !== null) ?? [],
    [podcasts],
  );

  const [internalTrackIndex, setInternalTrackIndex] = useState(0);
  const trackIndex =
    externalTrackIndex !== undefined ? externalTrackIndex : internalTrackIndex;

  const setTrackIndex = useCallback(
    (updater: number | ((prev: number) => number)) => {
      if (onExternalTrackIndexChange) {
        const nextVal =
          typeof updater === "function" ? updater(trackIndex) : updater;
        onExternalTrackIndexChange(nextVal);
      } else {
        setInternalTrackIndex(updater);
      }
    },
    [onExternalTrackIndexChange, trackIndex],
  );

  useEffect(() => {
    if (selectedTrackId !== undefined && tracks.length > 0) {
      const targetIndex = tracks.findIndex((t) => t.id === selectedTrackId);
      if (targetIndex !== -1 && targetIndex !== trackIndex) {
        setTrackIndex(targetIndex);
        setProgress(0);
      }
    }
  }, [selectedTrackId, tracks, trackIndex, setTrackIndex]);

  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const playing = isControlled ? isPlaying : internalPlaying;
  const hasTracks = tracks.length > 0;
  const safeIndex = hasTracks ? Math.min(trackIndex, tracks.length - 1) : 0;
  const currentTrack = hasTracks ? tracks[safeIndex] : null;
  const currentTrackId = currentTrack?.id;
  const isCurrentVideo = !!currentTrack?.src?.match(
    /\.(mp4|webm|mov|ogg|m3u8|mpd)(\?|$)/i,
  );

  const formatTime = (seconds: number) => {
    if (!seconds) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const getPlayerCurrentTime = useCallback(() => {
    const player = playerRef.current;
    if (!player) return 0;
    if (typeof player.getCurrentTime === "function")
      return player.getCurrentTime();
    if (typeof player.currentTime === "number") return player.currentTime;
    return 0;
  }, []);

  const getPlayerDuration = useCallback(() => {
    const player = playerRef.current;
    if (!player) return 0;
    if (typeof player.getDuration === "function") return player.getDuration();
    if (typeof player.duration === "number" && Number.isFinite(player.duration))
      return player.duration;
    return 0;
  }, []);

  const seekPlayerToSeconds = useCallback((seconds: number) => {
    const player = playerRef.current;
    if (!player) return false;
    if (typeof player.seekTo === "function") {
      player.seekTo(seconds, "seconds");
      return true;
    }
    if ("currentTime" in player) {
      player.currentTime = seconds;
      return true;
    }
    return false;
  }, []);

  const rewind = () => {
    const currentSeconds = getPlayerCurrentTime() || progress * duration;
    const nextSeconds = Math.max(currentSeconds - 15, 0);
    seekPlayerToSeconds(nextSeconds);
    setProgress(duration > 0 ? nextSeconds / duration : 0);
  };

  const forward = () => {
    const currentSeconds = getPlayerCurrentTime() || progress * duration;
    const nextSeconds = Math.min(
      currentSeconds + 15,
      duration || currentSeconds + 15,
    );
    seekPlayerToSeconds(nextSeconds);
    setProgress(duration > 0 ? nextSeconds / duration : progress);
  };

  const prevTrack = () => {
    const nextIndex = (trackIndex - 1 + tracks.length) % tracks.length;
    setTrackIndex(nextIndex);
    setProgress(0);
    if (setSelectedTrackId && tracks[nextIndex])
      setSelectedTrackId(tracks[nextIndex].id);
  };

  const nextTrack = () => {
    const nextIndex = (trackIndex + 1) % tracks.length;
    setTrackIndex(nextIndex);
    setProgress(0);
    if (!isControlled) {
      setInternalPlaying(true);
    }
    if (tracks[nextIndex]) {
      setSelectedTrackId(tracks[nextIndex].id);
    }
  };
  const handleSelectEpisode = (episodeId: number) => {
    const index = tracks.findIndex((track) => track.id === episodeId);
    if (index !== -1) {
      setTrackIndex(index);
      setProgress(0);
      if (setSelectedTrackId) setSelectedTrackId(episodeId);
      if (!isControlled) setInternalPlaying(true);
    }
  };

  useEffect(() => {
    if (!currentTrack || isCurrentVideo) return;

    const sync = () => {
      const current = getPlayerCurrentTime();
      const total = getPlayerDuration();

      if (total > 0) {
        setDuration(total);
        setProgress(current / total);
      } else if (current > 0) {
        setProgress(0);
      }
    };

    sync();
    const timer = window.setInterval(sync, 500);
    return () => window.clearInterval(timer);
  }, [
    currentTrackId,
    isCurrentVideo,
    getPlayerCurrentTime,
    getPlayerDuration,
    currentTrack,
  ]);

  const handleTogglePlay = () => {
    const player = playerRef.current;
    try {
      const duration = player?.getDuration?.();
      const current = player?.getCurrentTime?.();
      if (duration && current && Math.abs(duration - current) < 0.5) {
        player.seekTo(0);
      }
    } catch {}

    if (isControlled) {
      onTogglePlay?.();
      return;
    }
    setInternalPlaying((prev) => !prev);
  };

  const handleStopPlay = () => {
    onStopPlay?.();
    if (!isControlled) setInternalPlaying(false);
  };

  const handleSeek = (ratio: number) => {
    const activeDuration = getPlayerDuration() || duration;
    if (activeDuration <= 0) return;
    const nextSeconds = Math.min(
      activeDuration,
      Math.max(0, ratio * activeDuration),
    );
    seekPlayerToSeconds(nextSeconds);
    setDuration(activeDuration);
    setProgress(nextSeconds / activeDuration);
  };

  const handleEnded = () => {
    if (tracks.length > 1) {
      nextTrack();

      if (isControlled) {
        onTogglePlay?.();
      }
    } else {
      handleStopPlay();
    }
  };

  return (
    <div className="relative z-0 overflow-hidden xl:overflow-visible xl:flex items-center justify-between xl:gap-11 w-full xl:h-[108px] bg-transparent xl:bg-green-05 text-white p-2.5 xl:py-0 xl:pl-7 shadow-2xl rounded-[30px]">
      {currentTrack && (
        <>
          <div className="absolute inset-0 xl:hidden -z-10" aria-hidden="true">
            <div
              className="absolute inset-0 opacity-80 bg-center bg-cover scale-110"
              style={{ backgroundImage: `url(${currentTrack.cover})` }}
            />
            <div className="absolute inset-0 bg-green-05/80" />
          </div>

          <ReactPlayer
            ref={playerRef}
            src={currentTrack?.src}
            playing={playing && !isCurrentVideo}
            width="0"
            height="0"
            onEnded={handleEnded}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            }}
          />

          <button
            onClick={() => setIsMobileOpen(true)}
            className="flex items-center gap-3 text-right bg-transparent border-none p-0 cursor-pointer xl:pointer-events-none focus:outline-none w-full pr-1.5"
          >
            <div className="size-[70px] xl:size-20 shrink-0 bg-green-04 flex items-center justify-center overflow-hidden rounded-[20px]">
              {currentTrack.cover ? (
                <img
                  alt={currentTrack.title}
                  src={currentTrack.cover}
                  className="object-fill w-full h-full"
                />
              ) : (
                <div className="p-3.5">
                  <img
                    alt={currentTrack.title}
                    src={Logo.src}
                    className="object-fill"
                  />
                </div>
              )}
            </div>
            <div className="space-y-1 text-right text-white">
              <h2 className="font-semibold text-sm xl:text-base my-0">
                {currentTrack.title}
              </h2>
              <h2 className="line-clamp-1 text-xs opacity-70 my-0">
                {currentTrack.subtitle}
              </h2>
            </div>
          </button>

          <PlayBar
            playerRef={playerRef}
            currentTrack={currentTrack}
            episodes={tracks}
            isPlaying={playing}
            onTogglePlay={handleTogglePlay}
            onSelectEpisode={handleSelectEpisode}
            volume={volume}
            setVolume={setVolume}
            progress={progress}
            setProgress={setProgress}
            duration={duration}
            onSeek={handleSeek}
            formatTime={formatTime}
            rewind={rewind}
            forward={forward}
            nextTrack={nextTrack}
            prevTrack={prevTrack}
            onStopPlay={handleStopPlay}
            setDuration={setDuration}
            onToggleCollapsed={onToggleCollapsed}
            setIsVisible={setIsPlayerVisible ? setIsPlayerVisible : () => {}}
            mobileOpen={isMobileOpen}
            onTitleClick={setIsMobileOpen}
          />
          <CorePlayer
            playerRef={playerRef}
            currentTrack={currentTrack}
            isPlaying={playing}
            onTogglePlay={handleTogglePlay}
            onStopPlay={handleStopPlay}
            onClosePlayer={handleClosePlayer}
            progress={progress}
            setProgress={setProgress}
            duration={duration}
            setDuration={setDuration}
            volume={volume}
            setVolume={setVolume}
            formatTime={formatTime}
            rewind={rewind}
            forward={forward}
            nextTrack={nextTrack}
            prevTrack={prevTrack}
            onToggleCollapsed={onToggleCollapsed}
            setIsVisible={setIsPlayerVisible ? setIsPlayerVisible : () => {}}
          />
        </>
      )}
    </div>
  );
}
